/**
 * Password recovery landing page.
 *
 * Supabase's "Send password recovery" email points to:
 *   https://<site>/reset-password#access_token=<jwt>&refresh_token=<jwt>&type=recovery&...
 *
 * supabase-js's `detectSessionInUrl` is turned OFF in our client (to avoid
 * accidentally consuming hashes on every page), so this page parses the
 * hash manually, installs the recovery session via `setSession`, and shows
 * a "Set new password" form. After the password is saved we sign the user
 * out and bounce them back to `/login` to log in with the new credentials.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function parseHashParams() {
  // Strip leading '#' and parse like a query string
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const out = {};
  if (!hash) return out;
  for (const part of hash.split("&")) {
    const [k, v] = part.split("=");
    if (k) out[decodeURIComponent(k)] = v ? decodeURIComponent(v) : "";
  }
  return out;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | ready | saving | done | error
  const [errMsg, setErrMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);

  // 1) On mount, install the temporary recovery session from the URL hash.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const hp = parseHashParams();
        // Supabase appends e.g. error_description=Email+link+is+invalid+or+has+expired
        if (hp.error || hp.error_description) {
          setErrMsg(hp.error_description?.replace(/\+/g, " ") || hp.error || "Invalid or expired link");
          setStatus("error");
          return;
        }
        if (!hp.access_token || !hp.refresh_token) {
          setErrMsg(
            "This link is missing the recovery tokens. Please request a new password reset email and click the link from the email directly (do not refresh this page).",
          );
          setStatus("error");
          return;
        }
        const { error } = await supabase.auth.setSession({
          access_token: hp.access_token,
          refresh_token: hp.refresh_token,
        });
        if (cancelled) return;
        if (error) {
          setErrMsg(error.message || "Failed to verify the recovery link");
          setStatus("error");
          return;
        }
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e?.message || "Unexpected error verifying the recovery link");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setStatus("saving");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // We sign the user out so they have to re-authenticate with the new
      // password — this prevents the recovery session (which doesn't carry
      // a full role / profile in our backend) from accidentally leaving the
      // app in an inconsistent half-logged-in state.
      try { await supabase.auth.signOut({ scope: "local" }); } catch (_) { /* noop */ }
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith("sb-") || k === "token" || k === "user")
          .forEach((k) => localStorage.removeItem(k));
      } catch (_) { /* noop */ }
      setStatus("done");
      toast.success("Password updated. Please log in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      console.error("Password reset failed", err);
      const msg = err?.message || "Failed to update password";
      toast.error(msg);
      setStatus("ready");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardHeader>
          <CardTitle className="font-display text-2xl text-brand-navy">
            {status === "done" ? "All set!" : "Set a new password"}
          </CardTitle>
          <CardDescription>
            {status === "verifying" && "Verifying your recovery link…"}
            {status === "ready" && "Choose a new password to finish resetting your account."}
            {status === "saving" && "Saving your new password…"}
            {status === "done" && "Your password has been updated. Redirecting to login…"}
            {status === "error" && "We couldn't verify your recovery link."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === "verifying" && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-6 w-6 mr-2 animate-spin" />
              <span>Verifying…</span>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-3 text-sm text-red-800">
                {errMsg}
              </div>
              <p className="text-xs text-muted-foreground">
                Recovery emails expire after a short time. Ask an admin to send a fresh
                one (Supabase &rarr; Authentication &rarr; Users &rarr; <em>Send recovery</em>)
                or use the &quot;Forgot password?&quot; link on the login page.
              </p>
              <Button
                onClick={() => navigate("/login", { replace: true })}
                className="w-full"
                data-testid="reset-back-to-login"
                variant="outline"
              >
                Back to login
              </Button>
            </div>
          )}

          {(status === "ready" || status === "saving") && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    autoFocus
                    required
                    minLength={6}
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={show ? "Hide password" : "Show password"}
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat the new password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button
                type="submit"
                disabled={status === "saving"}
                className="w-full bg-brand-navy hover:bg-brand-navy-hover text-white"
                data-testid="reset-submit"
              >
                {status === "saving" ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  "Update password"
                )}
              </Button>
            </form>
          )}

          {status === "done" && (
            <div className="flex flex-col items-center py-8 text-green-700">
              <CheckCircle2 className="h-12 w-12 mb-3" />
              <p className="text-sm">Password updated. Redirecting…</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
